import { useCallback } from "react";

import { useDispatch } from "react-redux";

import {
  combineRunProps,
  parseErrorResponse,
  useRequestWithFeedback,
} from "../../../composites";
import { useObjectMemo } from "../../../hooks";
import { useLibrary } from "../../../providers";
import { REQUEST_STATUS } from "../../../composites/request/request.types";

import { actions } from "./slice";
import { API } from "./api";

import type {
  DualApprovalsPayload,
  DualApprovalPayload,
} from "../../../../types/models/v1";
import type {
  Data,
  ErrorResponse,
  OverrideRunProps,
} from "../../../composites/request/request.types";
import type { AnyException } from "../../../composites/request/request.helpers";

export const useFetchDualApprovals = () => {
  const dispatch = useDispatch();
  const { send, status, loading } =
    useRequestWithFeedback<DualApprovalsPayload>();

  const fetchDualApprovals = useCallback(() => {
    // we use .then() instead of onData because the hook's send() returns
    // the original response promise, while onData runs in a separate .then() chain.
    // this ensures dispatch happens before the caller's .then() receives data
    return send({
      action: API.fetchDualApprovals(),
    }).then((data) => {
      if (!data) {
        return undefined;
      }
      dispatch(actions.receive(data));
      return data;
    });
  }, [dispatch, send]);

  return useObjectMemo({
    send: fetchDualApprovals,
    loading,
    error: status === REQUEST_STATUS.error,
    hasData: false,
  });
};

export const useFetchPaginatedDualApprovals = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.FetchDualApproval.Response,
    API.FetchDualApproval.Error
  >();

  const dispatch = useDispatch();

  const fetchPage = useCallback(
    (
      pageNumber: number,
      pageSize: number,
      model?: string,
      modelNotIn?: string,
    ) => {
      return send({
        action: API.fetchDualApprovals(pageNumber, pageSize, model, modelNotIn),
      }).then((data) => {
        if (!data) {
          return undefined;
        }

        const {
          approval_requests: results,
          meta: { total },
        } = data;

        dispatch(
          actions.upsertPage({
            pageNumber,
            results,
            total,
          }),
        );

        return data;
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === REQUEST_STATUS.error,
    send: fetchPage,
    loading,
  });
};

export const useFetchPaginatedTemplateDualApprovals = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.FetchDualApproval.Response,
    API.FetchDualApproval.Error
  >();

  const dispatch = useDispatch();

  const fetchPage = useCallback(
    (pageNumber: number, pageSize: number) => {
      return send({
        action: API.fetchTemplateDualApprovals(pageNumber, pageSize),
      }).then((data) => {
        if (!data) {
          return undefined;
        }

        const {
          approval_requests: results,
          meta: { total },
        } = data;

        dispatch(
          actions.upsertTemplatePage({
            pageNumber,
            results,
            total,
          }),
        );

        return data;
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === REQUEST_STATUS.error,
    send: fetchPage,
    loading,
  });
};

export const useFetchPaginatedHistoryDualApprovals = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.FetchDualApproval.Response,
    API.FetchDualApproval.Error
  >();

  const dispatch = useDispatch();

  const fetchPage = useCallback(
    (pageNumber: number, pageSize: number) => {
      send({
        action: API.fetchHistoryDualApprovals(pageNumber, pageSize),
        onData: ({ approval_requests: results, meta: { total } }) => {
          dispatch(
            actions.upsertHistoryPage({
              pageNumber,
              results,
              total,
            }),
          );
        },
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === REQUEST_STATUS.error,
    send: fetchPage,
    loading,
  });
};

export const useFetchDualApprovalById = () => {
  const { send, status, loading } =
    useRequestWithFeedback<DualApprovalPayload>();
  const dispatch = useDispatch();

  const fetchDualApprovalById = useCallback(
    (id: API.DualApprovalId) => {
      return send({
        action: API.fetchDualApprovalById(id),
      }).then((data) => {
        if (!data) {
          return undefined;
        }

        dispatch(actions.receive(data));
        return data.approval_request;
      });
    },
    [dispatch, send],
  );

  return useObjectMemo({
    send: fetchDualApprovalById,
    loading,
    error: status === REQUEST_STATUS.error,
  });
};

const useActionDualApproval = (action: "approve" | "reject") => {
  const { send, status, loading } =
    useRequestWithFeedback<DualApprovalPayload>();
  const dispatch = useDispatch();

  const actionDualApproval = useCallback(
    (id: API.DualApprovalId, note?: string) => {
      const requestAction =
        action === "approve"
          ? API.approveDualApproval(id)
          : API.rejectDualApproval(id, note);

      return send({
        action: requestAction,
      }).then((data) => {
        if (!data) {
          return undefined;
        }
        dispatch(actions.receive(data));
        return data;
      });
    },
    [action, dispatch, send],
  );

  return useObjectMemo({
    send: actionDualApproval,
    loading,
    error: status === REQUEST_STATUS.error,
  });
};

export const useApproveDualApproval = () => {
  return useActionDualApproval("approve");
};

export const useRejectDualApproval = () => {
  return useActionDualApproval("reject");
};

export const useCancelDualApproval = () => {
  const { send, status, loading } = useRequestWithFeedback();
  const l10n = useLibrary("translations");
  const { throwToast } = useLibrary("toasts");

  const cancelDualApproval = useCallback(
    (
      id: API.DualApprovalId,
      model: string,
      overrideRunProps: OverrideRunProps = {},
    ) => {
      // accept both model strings: azul passes the backend model name
      // "UnverifiedACHReversal", while atlas passes a synthetic
      // "ACHReversal" discriminator on its locally-built payment rows. both
      // must be handled here or reversal toasts fall back to wire copy
      const isAchReversalModel =
        model === "ACHReversal" || model === "UnverifiedACHReversal";

      send({
        action: API.cancelDualApproval(id),
        ...combineRunProps(
          {
            onSuccess: async () => {
              let successMessage = l10n.getString(
                "payment-activity-domestic-wire-canceled",
                null,
                "Domestic wire canceled.",
              );

              if (isAchReversalModel) {
                successMessage = l10n.getString(
                  "payment-activity-ach-reversal-canceled",
                  null,
                  "ACH reversal canceled.",
                );
              } else if (model === "SwiftWireTransaction") {
                successMessage = l10n.getString(
                  "payment-activity-international-wire-canceled",
                  null,
                  "International wire canceled.",
                );
              } else if (
                model === "WireImportBatch" ||
                model === "UnverifiedACHTransaction"
              ) {
                successMessage = l10n.getString(
                  "delete-payment-success-message",
                  null,
                  "Payment canceled.",
                );
              }

              throwToast({
                kind: "success",
                message: successMessage,
              });
            },
            onError: async (error: ErrorResponse<AnyException<Data>>) => {
              const { errors } = await parseErrorResponse(error);
              const defaultError = isAchReversalModel
                ? l10n.getString(
                    "payment-activity-cancel-ach-reversal-error",
                    null,
                    "We could not cancel your ACH reversal. Please try again.",
                  )
                : l10n.getString(
                    "payment-activity-cancel-wire-error",
                    null,
                    "We could not cancel your payment. Please try again.",
                  );
              throwToast({
                kind: "error",
                message: errors.length
                  ? `${errors[0].description}`
                  : defaultError,
              });
            },
          },
          overrideRunProps,
        ),
      });
    },
    [send, l10n, throwToast],
  );

  return useObjectMemo({
    cancelDualApproval,
    loading,
    error: status === REQUEST_STATUS.error,
  });
};
